import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../views/firebase/firebase'; // Adjust the import path
import { onAuthStateChanged } from 'firebase/auth';
import Spinner from '../../components/Spinner';
import { getAuth, signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Logger from '../../utils/Logger';

const logAuthDebug = (...args) => {
  if (import.meta.env.DEV) {
    console.debug('[AuthUserContext]', ...args);
  }
};

const AuthUserContext = createContext({
  authUser: null,
  setAuthUser: () => {},
  isTokenValid: false,
  authMethod: null,
  loading: true,
  setAuthMethod: () => {},
  getValidToken: async () => null,
  isTokenExpired: (token) => true,
  refreshToken: async () => null,
  handleAuthError: () => {}
});

const AuthUserProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(undefined);
  const [authMethod, setAuthMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  // Define isTokenExpired as a stable function reference
  const isTokenExpired = useCallback((token) => {
    //Logger.debug('isTokenExpired called in conetxt.js with token:', token);
    if (!token) {
      Logger.debug('isTokenExpired token is null');
      return true;
    }
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = tokenData.exp * 1000;
      return Date.now() >= expirationTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }, []);

  // Define other functions as useCallback
  const refreshToken = useCallback(async () => {
    if (!auth.currentUser) throw new Error('No authenticated user found');
    try {
      
      const newToken = await auth.currentUser.getIdToken(true);
      setIsTokenValid(true);
      return newToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleAuthError(error);
      return null;
    }
  }, [auth]);

  const handleAuthError = useCallback((error) => {
    console.error('Authentication error:', error);
    switch (error.code) {
      case 'auth/requires-recent-login':
        toast.error('Please sign in again to continue');
        signOut(auth);
        break;
      case 'auth/id-token-expired':
        toast.error('Your session has expired. Please sign in again');
        signOut(auth);
        break;
      default:
        toast.error('Authentication error. Please try again');
    }
    setIsTokenValid(false);
  }, [auth]);

  const getValidToken = useCallback(async () => {
    if (!auth.currentUser) {
      Logger.debug('getValidToken auth.currentUser is null');
      logAuthDebug('getValidToken: no auth.currentUser');
      return null;
    }
    try {
      const currentToken = await auth.currentUser.getIdToken(false); // false means don't force refresh
      if (isTokenExpired(currentToken)) {
        Logger.debug('getValidToken isTokenExpired is true');
        logAuthDebug('getValidToken: token expired, refreshing');
        return await refreshToken();
      }
      setIsTokenValid(true);
      logAuthDebug('getValidToken: token ok', { uid: auth.currentUser.uid });
      return currentToken;
    } catch (error) {
      logAuthDebug('getValidToken: error', { message: error?.message || String(error) });
      handleAuthError(error);
      return null;
    }
  }, [auth, isTokenExpired, refreshToken, handleAuthError]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      logAuthDebug('onAuthStateChanged fired', { hasUser: Boolean(user), uid: user?.uid || null });
      if (user) {
        try {
          // Get single token with force refresh
          const token = await user.getIdToken(true);
          logAuthDebug('onAuthStateChanged: refreshed token', { uid: user.uid });
          
          setAuthUser({
            uid: user.uid,
            email: user.email,
            token,
            stsTokenManager: {
              accessToken: token // Keep them in sync
            },
            providerData: user.providerData,
            getValidToken: async () => await user.getIdToken(),
            isTokenExpired: (token) => isTokenExpired(token)
          });

          setIsTokenValid(true);
          
          // Debug log
         // console.log('Token set:', token);
        } catch (error) {
          logAuthDebug('onAuthStateChanged: token fetch error', { message: error?.message || String(error) });
          console.error('Error getting token:', error);
          handleAuthError(error);
        }
      } else {
        // Handle non-Firebase auth cases
        const userSession = JSON.parse(localStorage.getItem('userSession'));
        setIsTokenValid(false);
        
        if (userSession && userSession.walletAddress) {
          logAuthDebug('onAuthStateChanged: using web3 userSession');
          // Web3 auth case
          setAuthUser({
            uid: userSession.walletAddress,
            walletAddress: userSession.walletAddress,
            email: userSession.email || `${userSession.walletAddress}@metamask.eth`,
            providerData: [{ providerId: 'web3' }],
            getValidToken: async () => userSession.token,
            isTokenExpired: (token) => isTokenExpired(token)
          });
          setAuthMethod('web3');
        } else if (userSession && userSession.mieAppleToken) {
          logAuthDebug('onAuthStateChanged: using apple userSession');
          // Apple auth case
          setAuthUser({
            uid: userSession.mieUid,
            email: userSession.mieEmail,
            appleToken: userSession.mieAppleToken,
            providerData: [{ providerId: 'apple.com' }],
            getValidToken: async () => userSession.mieAppleToken,
            isTokenExpired: (token) => isTokenExpired(token)
          });
          setAuthMethod('apple');
        } else {
          Logger.debug('setAuthUser null');
          logAuthDebug('onAuthStateChanged: no session found, clearing authUser');
          setAuthUser(null);
          setAuthMethod(null);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, isTokenExpired, handleAuthError]);

  // Create a stable context value
  const contextValue = useMemo(() => {
    const value = {
      authUser,
      setAuthUser,
      isTokenValid,
      authMethod,
      loading,
      setAuthMethod,
      getValidToken,
      isTokenExpired,
      refreshToken,
      handleAuthError
    };
    
    // Debug log to verify all functions are included
    // Logger.debug('Creating context value with functions:', {
    //   hasIsTokenExpired: !!value.isTokenExpired,
    //   hasGetValidToken: !!value.getValidToken,
    //   allKeys: Object.keys(value),
    //   functionKeys: Object.keys(value).filter(key => typeof value[key] === 'function')
    // });

    return value;
  }, [
    authUser,
    setAuthUser,
    isTokenValid,
    authMethod,
    loading,
    setAuthMethod,
    getValidToken,
    isTokenExpired,
    refreshToken,
    handleAuthError
  ]);

  // Verify context value before rendering
  // useEffect(() => {
  //   Logger.debug('Context value updated:', {
  //     hasIsTokenExpired: !!contextValue.isTokenExpired,
  //     hasGetValidToken: !!contextValue.getValidToken,
  //     allFunctions: Object.keys(contextValue).filter(key => typeof contextValue[key] === 'function')
  //   });
  // }, [contextValue]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <AuthUserContext.Provider value={contextValue}>
      {children}
    </AuthUserContext.Provider>
  );
};

export { AuthUserContext, AuthUserProvider };
